// Hook para buscar dados EXCLUSIVAMENTE do Sentry via Edge Function
// Replicando a estrutura do Sentry Frontend Insights

import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCurrentAppUser } from './useCurrentAppUser';
import type { 
  SentryPerformanceData, 
  PerformanceFilters,
  DataSource,
  MetricScores,
  TransactionData,
  TimeSeries,
  SentryOperationalStatus,
  SentryError,
  SentryIssueActionLog,
  SentryIssueActionType,
} from '@/types/performance';

const SUPABASE_URL = 'https://irtygpadfbavrqhldhzm.supabase.co';

interface SentryProxyResponse {
  source: DataSource;
  timestamp: string;
  period: string;
  projectId?: string;
  data?: {
    webVitals: {
      lcp: number | null;
      fcp: number | null;
      cls: number | null;
      fid: number | null;
      inp: number | null;
      ttfb: number | null;
    };
    metricScores: MetricScores;
    performanceScore: number;
    stats: {
      errorCount: number;
      transactionCount: number;
      throughput: number;
    };
    errors: Array<{
      id: string;
      title: string;
      culprit: string;
      count: number;
      userCount: number;
      firstSeen: string;
      lastSeen: string;
      level: string;
      status: string;
      type: string;
      message: string;
      filename?: string;
      permalink: string;
      sentryStatus?: string;
      lastActionAt?: string | null;
      lastActionBy?: string | null;
      lastActionResult?: 'success' | 'failed' | null;
      lastActionType?: SentryIssueActionType | null;
      actionHistory?: SentryIssueActionLog[];
    }>;
    pages: Array<{
      path: string;
      lcp: number | null;
      fcp: number | null;
      cls: number | null;
      ttfb: number | null;
      inp: number | null;
      samples: number;
    }>;
    transactions: TransactionData[];
    timeSeries: TimeSeries;
  };
  error?: string;
}

async function fetchFromSentryProxy(period: string, issueStatus: PerformanceFilters['issueStatus'] = 'unresolved'): Promise<SentryProxyResponse> {
  const response = await fetch(
    `${SUPABASE_URL}/functions/v1/sentry-proxy?action=all&period=${period}&issueStatus=${issueStatus}`,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Sentry proxy error: ${response.status} - ${errorText}`);
  }

  return response.json();
}

type IssueUpdateStatus = 'resolved' | 'ignored' | 'unresolved' | 'marked_monitoring';

async function updateIssuesViaSentryProxy(
  issueIds: string[],
  status: IssueUpdateStatus,
  actor?: { userId?: string | null; name?: string | null },
  issueTitles?: Record<string, string>,
) {
  const response = await fetch(`${SUPABASE_URL}/functions/v1/sentry-proxy?action=update-issues`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      issueIds,
      status,
      actorUserId: actor?.userId ?? null,
      actorName: actor?.name ?? null,
      issueTitles: issueTitles ?? {},
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    if (response.status === 403) {
      throw new Error('Sentry token sem permissão para atualizar issues. Adicione pelo menos o escopo event:write.');
    }
    throw new Error(`Sentry proxy update error: ${response.status} - ${errorText}`);
  }

  const payload = await response.json();
  if (Array.isArray(payload?.failed) && payload.failed.length === issueIds.length) {
    const firstFailure = payload.failed[0];
    const message = String(firstFailure?.error || 'Sentry rejected the issue update');
    if (message.includes('403')) {
      throw new Error('Sentry token sem permissão para atualizar issues. Adicione pelo menos o escopo event:write.');
    }
    throw new Error(message);
  }

  return payload;
}

function getOperationalStatus(
  sentryStatus: string | undefined,
  lastSeen: string,
  fetchedAt: string,
  lastActionType?: SentryIssueActionType | null,
  lastActionResult?: 'success' | 'failed' | null,
): SentryOperationalStatus {
  if (sentryStatus === 'resolved') return 'resolved';
  if (sentryStatus === 'ignored') return 'ignored';

  const lastSeenAt = new Date(lastSeen).getTime();
  const fetchedAtMs = new Date(fetchedAt).getTime();
  if (Number.isNaN(lastSeenAt) || Number.isNaN(fetchedAtMs)) return 'needs_fix';

  const elapsedMs = fetchedAtMs - lastSeenAt;
  if (elapsedMs >= 24 * 60 * 60 * 1000) return 'ready_to_close';
  if (lastActionType === 'marked_monitoring' && lastActionResult === 'success') return 'monitoring';
  if (elapsedMs >= 6 * 60 * 60 * 1000) return 'monitoring';
  return 'needs_fix';
}

function applyOptimisticIssueTransition(
  data: SentryPerformanceData | null | undefined,
  issueIds: string[],
  nextStatus: IssueUpdateStatus,
  issueStatusFilter: PerformanceFilters['issueStatus'] = 'unresolved',
  actorName?: string | null,
): SentryPerformanceData | null | undefined {
  if (!data) return data;

  const issueSet = new Set(issueIds);
  const shouldKeepIssue = (error: SentryError) => {
    const statusAfterUpdate = issueSet.has(error.id)
      ? nextStatus === 'marked_monitoring' ? (error.sentryStatus || error.status || 'unresolved') : nextStatus
      : (error.sentryStatus || error.status || 'unresolved');
    if (issueStatusFilter === 'history') return statusAfterUpdate === 'resolved' || statusAfterUpdate === 'ignored';
    if (issueStatusFilter === 'all') return true;
    return statusAfterUpdate === (issueStatusFilter || 'unresolved');
  };

  const nextErrors: SentryError[] = data.errors
    .map((error) => {
      if (!issueSet.has(error.id)) return error;
      const resolvedSentryStatus =
        nextStatus === 'marked_monitoring' ? (error.sentryStatus || error.status || 'unresolved') : nextStatus;
      const lastActionType: SentryIssueActionType =
        nextStatus === 'unresolved' ? 'reopened' : nextStatus;
      const operationalStatus =
        nextStatus === 'marked_monitoring'
          ? 'monitoring'
          : nextStatus === 'resolved'
            ? 'resolved'
            : nextStatus === 'ignored'
              ? 'ignored'
              : getOperationalStatus('unresolved', error.lastSeen, data.fetchedAt || new Date().toISOString(), 'reopened', 'success');
      return {
        ...error,
        status: resolvedSentryStatus,
        sentryStatus: resolvedSentryStatus,
        operationalStatus,
        lastActionAt: new Date().toISOString(),
        lastActionBy: actorName ?? error.lastActionBy ?? null,
        lastActionResult: 'success' as const,
        lastActionType,
        actionHistory: [
          {
            id: `optimistic-${error.id}-${Date.now()}`,
            issueId: error.id,
            action: lastActionType,
            actorNameSnapshot: actorName ?? null,
            resultStatus: 'success' as const,
            createdAt: new Date().toISOString(),
          },
          ...(error.actionHistory || []),
        ].slice(0, 10),
      };
    })
    .filter(shouldKeepIssue);

  return {
    ...data,
    errors: nextErrors,
    stats: {
      ...data.stats,
      errorCount: nextErrors.length,
      unresolvedIssueCount: nextErrors.filter((error) => {
        const status = error.sentryStatus || error.status;
        return status !== 'resolved' && status !== 'ignored';
      }).length,
      readyToResolveCount: nextErrors.filter((error) => error.operationalStatus === 'ready_to_close').length,
    },
  };
}

function transformProxyResponse(response: SentryProxyResponse): SentryPerformanceData | null {
  // If Sentry is not configured or there's an error, return null
  if (response.source === 'not-configured' || response.source === 'error') {
    console.log('[Sentry] Not configured or error:', response.error);
    return null;
  }

  // If no data, return null
  if (!response.data) {
    console.log('[Sentry] No data in response');
    return null;
  }

  const { webVitals, metricScores, performanceScore, stats, errors, pages, transactions, timeSeries } = response.data;
  const mappedErrors = errors.map((e) => {
    const operationalStatus = getOperationalStatus(
      e.sentryStatus || e.status,
      e.lastSeen,
      response.timestamp,
      e.lastActionType,
      e.lastActionResult,
    );
    return {
      id: e.id,
      title: e.title,
      culprit: e.culprit,
      count: e.count,
      userCount: e.userCount,
      firstSeen: e.firstSeen,
      lastSeen: e.lastSeen,
      level: e.level as 'error' | 'warning' | 'info',
      status: e.sentryStatus || e.status,
      sentryStatus: e.sentryStatus || e.status,
      operationalStatus,
      permalink: e.permalink,
      type: e.type,
      message: e.message,
      filename: e.filename,
      lastActionAt: e.lastActionAt ?? null,
      lastActionBy: e.lastActionBy ?? null,
      lastActionResult: e.lastActionResult ?? null,
      lastActionType: e.lastActionType ?? null,
      actionHistory: e.actionHistory ?? [],
    };
  });
  const unresolvedIssueCount = mappedErrors.filter((e) => e.sentryStatus !== 'resolved' && e.sentryStatus !== 'ignored').length;
  const readyToResolveCount = mappedErrors.filter((e) => e.operationalStatus === 'ready_to_close').length;

  return {
    webVitals: {
      lcp: webVitals.lcp,
      fcp: webVitals.fcp,
      cls: webVitals.cls,
      fid: webVitals.fid,
      inp: webVitals.inp,
      ttfb: webVitals.ttfb,
    },
    metricScores: metricScores || {
      lcp: 50, fcp: 50, cls: 50, fid: 50, inp: 50, ttfb: 50
    },
    performanceScore: performanceScore || 50,
    stats: {
      errorCount: stats.errorCount,
      transactionCount: stats.transactionCount,
      throughput: stats.throughput,
      unresolvedIssueCount,
      readyToResolveCount,
    },
    errors: mappedErrors,
    pages: pages.map((p) => ({
      path: p.path,
      lcp: p.lcp,
      fcp: p.fcp,
      cls: p.cls,
      ttfb: p.ttfb,
      inp: p.inp ?? null,
      samples: p.samples,
    })),
    transactions: transactions || [],
    timeSeries: timeSeries || { throughput: [], duration: [] },
    dataSource: response.source,
    fetchedAt: response.timestamp,
  };
}

export function useSentryPerformance(filters: PerformanceFilters) {
  const issueStatus = filters.issueStatus || 'unresolved';
  return useQuery({
    queryKey: ['sentry-performance', filters],
    queryFn: async (): Promise<SentryPerformanceData | null> => {
      console.log('[Sentry] Fetching from Edge Function proxy...');
      
      try {
        const proxyResponse = await fetchFromSentryProxy(filters.period, issueStatus);
        console.log('[Sentry] Proxy response source:', proxyResponse.source);
        
        const data = transformProxyResponse(proxyResponse);
        
        if (data) {
          console.log('[Sentry] Transformed data:', {
            webVitals: data.webVitals,
            performanceScore: data.performanceScore,
            stats: data.stats,
            errorsCount: data.errors.length,
            pagesCount: data.pages.length,
            transactionsCount: data.transactions.length,
          });
        }
        
        return data;
      } catch (error) {
        console.error('[Sentry] Failed to fetch:', error);
        return null;
      }
    },
    staleTime: 60000, // 1 minute
    refetchInterval: 300000, // 5 minutes
    retry: 1,
  });
}

export function useUpdateSentryIssues(filters: PerformanceFilters) {
  const queryClient = useQueryClient();
  const { data: currentAppUser } = useCurrentAppUser();

  return useMutation({
    mutationFn: async ({
      issueIds,
      status,
      issueTitles,
    }: {
      issueIds: string[];
      status: IssueUpdateStatus;
      issueTitles?: Record<string, string>;
    }) => {
      return updateIssuesViaSentryProxy(issueIds, status, {
        userId: currentAppUser?.id ?? null,
        name: currentAppUser?.display_name || currentAppUser?.first_name || currentAppUser?.email || null,
      }, issueTitles);
    },
    onMutate: async ({ issueIds, status }) => {
      await queryClient.cancelQueries({ queryKey: ['sentry-performance'] });
      const previous = queryClient.getQueriesData<SentryPerformanceData | null>({ queryKey: ['sentry-performance'] });

      previous.forEach(([queryKey, data]) => {
        const filtersFromKey = (queryKey as unknown[])[1] as PerformanceFilters | undefined;
        const issueStatus = filtersFromKey?.issueStatus || 'unresolved';
        queryClient.setQueryData(
          queryKey,
          applyOptimisticIssueTransition(
            data,
            issueIds,
            status,
            issueStatus,
            currentAppUser?.display_name || currentAppUser?.first_name || currentAppUser?.email || null,
          ),
        );
      });

      return { previous };
    },
    onSuccess: (payload, variables) => {
      const successCount = Array.isArray(payload?.updated) ? payload.updated.length : 0;
      const failureCount = Array.isArray(payload?.failed) ? payload.failed.length : 0;

      if (failureCount > 0 && successCount > 0) {
        toast(`Ação parcial: ${successCount} atualizada(s), ${failureCount} falhou/falharam.`);
      } else if (failureCount > 0) {
        const firstFailure = payload.failed?.[0]?.error || 'Falha ao atualizar issues no Sentry';
        toast.error(String(firstFailure));
      } else if (variables.status === 'marked_monitoring') {
        toast.success('Issue marcada como corrigida e em monitoramento');
      } else {
        toast.success('Status das issues atualizado no Sentry');
      }
      queryClient.invalidateQueries({ queryKey: ['sentry-performance'] });
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: ['sentry-performance'] });
      toast.error(error instanceof Error ? error.message : 'Falha ao atualizar issue no Sentry');
    },
  });
}
